import { memo, useCallback } from 'react';
import { IconClose, IconLink } from '../../../../icons';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../popovers/popover/Popover';
import Button, { ButtonVariant } from '../../../../atoms/buttons/button/Button';
import { TiptapRange, useTiptap } from '../../Tiptap';
import { type Editor, getMarkRange, useEditorState } from '@tiptap/react';
import TiptapLinkForm from './tiptap-link-form';
import TiptapToolbarButton from '../tiptap-toolbar-button';

const getValidLinkUrl = (url: string) => (url.includes(':') ? url : 'https://' + url);

const getValidLinkText = (url: string, text?: string) => {
  if (!text) {
    return url;
  }

  const trimmedText = text.trim();

  return trimmedText.length > 0 ? trimmedText : url;
};

export type LinkEditorState =
  | {
      selectionIsLink: false;
      selectionRange: TiptapRange;
      selectionText: string;
    }
  | {
      selectionIsLink: true;
      selectionLinkMarkRange: TiptapRange;
      selectionLinkHref: string;
      selectionLinkText: string;
    };

const TiptapLink = memo(() => {
  const { editor, linkPopoverOpen, handleSetLinkPopoverOpen } = useTiptap();

  const editorState: LinkEditorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => {
      const selectionRange = editor.state.selection;
      const selectionStartPos = editor.state.doc.resolve(selectionRange.from);

      const linkType = editor.schema.marks.link;
      const selectionLinkMarkRange = getMarkRange(selectionStartPos, linkType);
      const selectionLinkMark = selectionStartPos
        .marks()
        .find((mark) => mark.type === linkType);

      if (
        !selectionLinkMarkRange ||
        !selectionLinkMark ||
        !selectionLinkMark.attrs.href
      ) {
        return {
          selectionIsLink: false,
          selectionRange,
          selectionText: editor.state.doc.textBetween(
            selectionRange.from,
            selectionRange.to,
          ),
        };
      }

      return {
        selectionIsLink: true,
        selectionLinkMarkRange,
        selectionLinkHref: selectionLinkMark.attrs.href,
        selectionLinkText: editor.state.doc.textBetween(
          selectionLinkMarkRange.from,
          selectionLinkMarkRange.to,
        ),
      };
    },
  });

  const addLink = useCallback(
    (linkUrl: string, linkText?: string) => {
      const validLinkUrl = getValidLinkUrl(linkUrl);
      const validLinkText = getValidLinkText(validLinkUrl, linkText);

      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: validLinkText,
          marks: [
            {
              type: 'link',
              attrs: {
                href: validLinkUrl,
                target: '_blank',
              },
            },
          ],
        })
        .run();

      handleSetLinkPopoverOpen(false);
    },
    [editor, handleSetLinkPopoverOpen],
  );

  const updateLink = useCallback(
    (range: TiptapRange, linkUrl: string, linkText?: string) => {
      const validLinkUrl = getValidLinkUrl(linkUrl);
      const validLinkText = getValidLinkText(validLinkUrl, linkText);

      editor
        .chain()
        .focus()
        .setTextSelection(range)
        .deleteSelection()
        .insertContent({
          type: 'text',
          text: validLinkText,
          marks: [
            {
              type: 'link',
              attrs: {
                href: validLinkUrl,
                target: '_blank',
              },
            },
          ],
        })
        .run();

      handleSetLinkPopoverOpen(false);
    },
    [editor, handleSetLinkPopoverOpen],
  );

  const removeLink = useCallback(() => {
    if (!editorState.selectionIsLink) {
      return;
    }

    editor
      .chain()
      .focus()
      .setTextSelection(editorState.selectionLinkMarkRange)
      .unsetLink()
      .run();

    handleSetLinkPopoverOpen(false);
  }, [editor, editorState, handleSetLinkPopoverOpen]);

  return (
    <Popover
      open={linkPopoverOpen}
      onOpenChange={(open) => handleSetLinkPopoverOpen(open)}
    >
      <PopoverTrigger asChild>
        <TiptapToolbarButton
          aria-label={editorState.selectionIsLink ? 'éditer le lien' : 'ajouter un lien'}
          icon={IconLink}
          tooltip={editorState.selectionIsLink ? 'Éditer le lien' : 'Ajouter un lien'}
          isActive={editorState.selectionIsLink}
        />
      </PopoverTrigger>

      <PopoverContent
        close={false}
        className="flex w-[370px] flex-col gap-4 rounded-2xl p-5"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-base font-semibold">
            {editorState.selectionIsLink ? (
              <span>Éditer le lien</span>
            ) : (
              <span>Ajouter un lien</span>
            )}
          </span>

          <PopoverClose asChild>
            <Button variant={ButtonVariant.DEFAULT} className="p-1.5">
              <IconClose />
            </Button>
          </PopoverClose>
        </div>

        <TiptapLinkForm
          editorState={editorState}
          addLink={addLink}
          updateLink={updateLink}
          removeLink={removeLink}
        />
      </PopoverContent>
    </Popover>
  );
});

TiptapLink.displayName = 'Tiptap.Toolbar.Link';

export default TiptapLink;
